import React from "react";

import DestinationSection, {
  DestinationSectionProps,
} from "./destinations/DestinationSection";
import {
  BriefcaseBusinessIcon,
  BuildingIcon,
  DoorOpenIcon,
  GlobeIcon,
  GraduationCapIcon,
  SchoolIcon,
  ShieldCheckIcon,
  UsersRoundIcon,
} from "lucide-react";
import { LanguageIcon } from "@heroicons/react/24/solid";
import {
  destinationAustralia,
  destinationCanada,
  destinationIreland,
  destinationUK,
  destinationUSA,
} from "../../../../../images/web/study-abroad";

const Destinations = () => {
  const destinations: DestinationSectionProps[] = [
    {
      destination: "australia",
      headline: (
        <span>
          Study in{" "}
          <span className="text-blue-00 italic mx-1">Australia</span>
          : <br /> Where Innovation Shines Down Under
        </span>
      ),
      description:
        "Renowned for research excellence and a laid-back lifestyle, Australia attracts students seeking high-quality education in a stunning environment. With strong government support for international students and robust post-study work options, Australian universities deliver exceptional returns on your educational investment.",
      highlights: [
        {
          icon: <GraduationCapIcon />,
          text: "Temporary Graduate visa (2–4 years post-study)",
        },
        {
          icon: <BriefcaseBusinessIcon />,
          text: "Part-time work during studies",
        },
        {
          icon: <ShieldCheckIcon />,
          text: "High quality of life and safety",
        },
        {
          icon: <SchoolIcon />,
          text: "Nine universities in global top 100",
        },
        {
          icon: <UsersRoundIcon />,
          text: "Vibrant international student community",
        },
      ],
      layoutVariant: "default" as const,
      imageSrc: destinationAustralia,
      imageAlt:
        "The iconic sandstone architecture of the University of Sydney Quadrangle on a clear day, a symbol of academic excellence.",
      fromColor: "from-blue-00/50!",
      toColor: "to-blue-ea!",
    },
    {
      destination: "canada",
      headline: (
        <span>
          Study in{" "}
          <span className="text-blue-00 italic mx-1">Canada</span>
          : <br /> Innovation Meets Opportunity
        </span>
      ),
      description:
        "Home to globally ranked universities and a welcoming immigration policy, Canada offers exceptional education with post-graduation work opportunities. From the research powerhouses of Toronto and Vancouver to the bilingual charm of Montreal, Canadian institutions provide world-class programs in technology, business, health sciences, and more.",
      highlights: [
        {
          icon: <BriefcaseBusinessIcon />,
          text: "Post-graduation work permits up to 3 years",
        },
        {
          icon: <DoorOpenIcon />,
          text: "Pathway to permanent residency",
        },
        {
          icon: <GraduationCapIcon />,
          text: "Affordable tuition compared to USA/UK",
        },
        {
          icon: <ShieldCheckIcon />,
          text: "Safe, multicultural environment",
        },
        {
          icon: <SchoolIcon />,
          text: "Globally recognized degrees",
        },
      ],

      layoutVariant: "reversed" as const,
      imageSrc: destinationCanada,
      imageAlt:
        "Stunning Gothic Revival building amidst lush greenery under a cloudy sky in Toronto",
      fromColor: "from-red-200!",
      toColor: "to-red-50!",
    },
    {
      destination: "ireland",
      headline: (
        <span>
          Study in{" "}
          <span className="text-blue-00 italic mx-1">Ireland</span>
          : <br /> Europe's Innovation Hub
        </span>
      ),
      description:
        "Ireland has emerged as Europe's tech and pharma capital while maintaining its reputation for warm hospitality and quality education. With English as the primary language and a thriving economy, Ireland offers students an ideal blend of career opportunities, cultural richness, and a high quality of life.",
      highlights: [
        {
          icon: <GlobeIcon />,
          text: "European tech hub (Google, Apple, Facebook HQs)",
        },
        {
          icon: <BriefcaseBusinessIcon />,
          text: "Two-year post-study work visa",
        },
        {
          icon: <LanguageIcon />,
          text: "English-speaking EU nation",
        },
        {
          icon: <BuildingIcon />,
          text: "Growing startup ecosystem",
        },
      ],
      imageSrc: destinationIreland,
      imageAlt:
        "A stunning view of a historic university building in Ireland with a prominent monument in front",
      layoutVariant: "default" as const,
      fromColor: "from-orange-200!",
      toColor: "to-orange-50!",
    },
    {
      destination: "united kingdom",
      headline: (
        <span>
          Study in the{" "}
          <span className="text-blue-00 italic mx-1">United Kingdom</span>
          : <br /> Tradition Meets Excellence
        </span>
      ),
      description:
        "With centuries of academic excellence, UK universities consistently rank among the world's best. Whether you're drawn to Oxford's historic spires or London's cutting-edge business schools, British education offers rigorous programs, innovative research opportunities, and a degree valued worldwide.",
      highlights: [
        {
          icon: <GraduationCapIcon />,
          text: "1-year master's programs save time and money",
        },
        {
          icon: <UsersRoundIcon />,
          text: "Rich cultural and historical experience",
        },
        {
          icon: <BriefcaseBusinessIcon />,
          text: "Strong industry connections and internships",
        },
        {
          icon: <ShieldCheckIcon />,
          text: "Graduate immigration route available",
        },
        {
          icon: <SchoolIcon />,
          text: "Home to 4 of the world's top 10 universities",
        },
      ],
      imageSrc: destinationUK,
      imageAlt: "Oxford University Museum of Natural History at Dusk",
      layoutVariant: "reversed" as const,
      fromColor: "from-blue-200!",
      toColor: "to-blue-50!",
    },
    {
      destination: "united states",
      headline: (
        <span>
          Study in the{" "}
          <span className="text-blue-00 italic mx-1">United States</span>
          : <br /> The land of Academic Opportunity
        </span>
      ),

      description:
        "With more top-ranked universities than any other nation, the USA offers unparalleled academic diversity and research funding. From Ivy League prestige to specialized tech institutes and liberal arts colleges, American higher education provides flexibility, innovation, and opportunities that can launch global careers.",
      highlights: [
        {
          icon: <SchoolIcon />,
          text: "Home to 50+ universities in global top 100",
        },
        {
          icon: <GraduationCapIcon />,
          text: "Extensive scholarship opportunities",
        },
        {
          icon: <BriefcaseBusinessIcon />,
          text: "Optional Practical Training (OPT) for work experience",
        },
        {
          icon: <ShieldCheckIcon />,
          text: "STEM OPT extension (3 years total)",
        },
        {
          icon: <GlobeIcon />,
          text: "Unmatched research resources and funding",
        },
      ],
      imageSrc: destinationUSA,
      imageAlt:
        "Scenic view of an Ivy League campus with historical architecture and lush greenery.",
      layoutVariant: "default" as const,
      fromColor: "from-red-300!",
      toColor: "to-white!",
    },
  ];

  return (
    <section className="py-20 px-4 md:px-8 bg-white" id="destinations-section">
      {/* Header */}
      <div className="text-center mb-16 max-w-3xl mx-auto">
        <h2 className="text-black-1a text-4xl md:text-5xl font-light tracking-tight mb-6">
          <span className="text-blue-00 italic mx-1">Destinations</span>
          We Serve.
        </h2>
        <p className="text-dark-6b text-base">
          We transform ambitious students into successful applicants across the
          globe's top academic hubs. Explore the destinations where your future
          begins.
        </p>
      </div>

      {/* Destination Cards Grid */}
      <DestinationSection {...destinations[0]} />
      <DestinationSection {...destinations[1]} />
      <DestinationSection {...destinations[2]} />
      <DestinationSection {...destinations[3]} />
      <DestinationSection {...destinations[4]} />
    </section>
  );
};

export default Destinations;
